import React from "react";
import { fmtEur } from "@/lib/format";

const Row = ({ label, value, bold }) => (
  <div className={`flex items-center justify-between px-4 py-2 ${bold ? "bg-slate-800/60 font-semibold" : ""}`}>
    <span className={`text-sm ${bold ? "text-white" : "text-slate-300"}`}>{label}</span>
    <span className="font-mono-fin text-sm text-white">{fmtEur(value)}</span>
  </div>
);

// Compact, dark, print-friendly summary of a rating analysis (for the combined PDF).
export function AnalysisPrintable({ analysis, lang = "it" }) {
  if (!analysis || !analysis.result) return null;
  const r = analysis.result;
  const sp = r.reclassification?.stato_patrimoniale || {};
  const ce = r.reclassification?.conto_economico || {};
  const rating = r.rating || {};
  const it = lang === "it";

  return (
    <div style={{ width: 980 }} className="bg-slate-950 text-white p-8 font-body">
      <div className="flex items-center gap-2 text-blue-400 text-sm font-semibold">
        {it ? "Analisi di Bilancio & Rating" : "Balance Sheet Analysis & Rating"}
      </div>
      <h1 className="mt-1 font-heading text-3xl font-bold text-white">{analysis.company_name}</h1>
      <p className="text-slate-400 mt-1">{it ? "Esercizio" : "Fiscal year"}: {analysis.fiscal_year}{analysis.sector ? ` · ${analysis.sector}` : ""}</p>

      <div className="mt-6 grid grid-cols-4 gap-4">
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <p className="text-xs uppercase tracking-wider text-slate-400">Rating</p>
          <p className="font-heading text-3xl font-extrabold text-white mt-1">{rating.grade}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <p className="text-xs uppercase tracking-wider text-slate-400">Score</p>
          <p className="font-mono-fin text-2xl font-bold text-white mt-1">{rating.score}/100</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <p className="text-xs uppercase tracking-wider text-slate-400">{it ? "Ricavi" : "Revenue"}</p>
          <p className="font-mono-fin text-lg font-semibold text-white mt-1">{fmtEur(ce.valore_produzione)}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <p className="text-xs uppercase tracking-wider text-slate-400">EBITDA</p>
          <p className="font-mono-fin text-lg font-semibold text-white mt-1">{fmtEur(ce.mol_ebitda)}</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-6">
        <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-800 font-semibold text-white text-sm">{it ? "Stato Patrimoniale" : "Balance Sheet"}</div>
          <Row label={it ? "Attivo corrente" : "Current assets"} value={sp.attivo_corrente} />
          <Row label={it ? "Attivo fisso" : "Fixed assets"} value={sp.attivo_fisso} />
          <Row label={it ? "TOTALE ATTIVO" : "TOTAL ASSETS"} value={sp.totale_attivo} bold />
          <Row label={it ? "Patrimonio netto" : "Equity"} value={sp.patrimonio_netto} />
          <Row label={it ? "Passivo corrente" : "Current liabilities"} value={sp.passivo_corrente} />
          <Row label="PFN" value={sp.posizione_finanziaria_netta} />
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-800 font-semibold text-white text-sm">{it ? "Conto Economico" : "Income Statement"}</div>
          <Row label={it ? "Valore della Produzione" : "Production value"} value={ce.valore_produzione} bold />
          <Row label={it ? "Valore Aggiunto" : "Value added"} value={ce.valore_aggiunto} />
          <Row label="MOL (EBITDA)" value={ce.mol_ebitda} bold />
          <Row label="EBIT" value={ce.ebit} />
          <Row label={it ? "Risultato ante imposte" : "Pre-tax result"} value={ce.risultato_ante_imposte} />
          <Row label={it ? "UTILE NETTO" : "NET INCOME"} value={ce.utile_netto} bold />
        </div>
      </div>

      {analysis.commentary && (
        <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900 p-6">
          <p className="font-heading text-base font-semibold text-white mb-3">{it ? "Commento Diagnostico (AI)" : "Diagnostic Commentary (AI)"}</p>
          {typeof analysis.commentary === "string" ? (
            <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">{analysis.commentary}</p>
          ) : (
            <div className="space-y-3">
              {[["executive_diagnosis", it ? "Diagnosi" : "Diagnosis"],
                ["executive_summary", it ? "Sintesi" : "Summary"],
                ["causal_chain", it ? "Catena causale" : "Causal chain"],
                ["rating_rationale", it ? "Motivazione rating" : "Rating rationale"]]
                .filter(([k]) => typeof analysis.commentary[k] === "string" && analysis.commentary[k])
                .map(([k, label]) => (
                  <div key={k}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-blue-400 mb-1">{label}</p>
                    <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">{analysis.commentary[k]}</p>
                  </div>
                ))}
              {[["strengths", it ? "Punti di forza" : "Strengths"],
                ["weaknesses", it ? "Criticità" : "Weaknesses"],
                ["operational_decisions_90d", it ? "Azioni 90 giorni" : "90-day actions"],
                ["strategic_decisions_12m", it ? "Strategia 12 mesi" : "12-month strategy"]]
                .filter(([k]) => Array.isArray(analysis.commentary[k]) && analysis.commentary[k].length)
                .map(([k, label]) => (
                  <div key={k}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-blue-400 mb-1">{label}</p>
                    <ul className="space-y-1">
                      {analysis.commentary[k].map((x, i) => (
                        <li key={`${k}-${i}`} className="text-slate-300 text-sm flex items-start gap-2">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" />
                          {typeof x === "string" ? x : JSON.stringify(x)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
